import axios from "axios";
import { END_POINT } from "../utils/End-points";
import { trackPromise } from "react-promise-tracker";
import { clearAccessToken, getToken } from "./tokenManager";
import {
  infoToster,
  errorToster as toastError,
  suppressNextErrorToster,
  suppressNextSuccessToster,
} from "../shared/toster/Toster";
import i18n from "i18next";

import store from "../store";

let isForcingLogout = false;
const isSessionKilledMessage = (msg = "") =>
  /session (was )?(revoked|ended)|session has been ended|revoked|account (is )?(inactive|deactivated|locked)|temporary password has expired/i.test(
    msg
  );

const forceLogout = (msg) => {
  if (isForcingLogout) return;
  isForcingLogout = true;
  try {
    clearAccessToken();
  } catch (_) {}
  try {
    sessionStorage.removeItem("user_key");
    sessionStorage.removeItem("hotel_key");
  } catch (_) {}
  toastError(msg || "Your session has ended. Please log in again.");
  if (typeof window !== "undefined" && !window.location.pathname.startsWith("/auth")) {
    setTimeout(() => {
      window.location.href = "/auth/login";
    }, 300);
  }
};

// Common headers
const formDataHeaders = {
  "Content-Type": "multipart/form-data",
  timezone: "Asia/Kolkata",
};

const jsonHeaders = {
  "Content-Type": "application/json",
  timezone: "Asia/Kolkata",
};

// Create axios instance
const createApiInstance = (
  baseURL,
  withCredentials = true,
  addToken = true
) => {
  const instance = axios.create({ baseURL, withCredentials });

  // Attach access token and active hotel_id before every request
  instance.interceptors.request.use(async (config) => {
    const token = addToken && await getToken();
    if (token && addToken) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    try {
      const activeHotelId = store?.getState?.()?.auth?.hotel?.hotel_id;
      if (activeHotelId && !config.headers.hotel_id) {
        config.headers.hotel_id = activeHotelId;
      }
    } catch (e) {}

    return config;
  });

  // Handle 401, refresh once, and detect permission changes
  instance.interceptors.response.use(
    (res) => {
      if (res.headers?.['x-permission-changed'] === 'true') {
        import('../apis/permission.service').then(({ fetchPermissions }) => {
          fetchPermissions();
        });
      }
      if (res.status === 202 && res.data?.approval_required) {
        infoToster(
          i18n.t("pages.approval_workflow.toast.submitted", {
            defaultValue: "Request submitted for approval.",
          })
        );
        res.data.__approvalPending = true;
        suppressNextSuccessToster();
        res.status = 200;
        return res;
      }
      return res;
    },
    async (error) => {
      const originalRequest = error.config || {};
      const status = error.response?.status;
      const msg = error.response?.data?.msg || "";
      const isLoginCall = (originalRequest.url || "").includes("auth/login");

      if (status === 409 && error.response?.data?.approval_pending) {
        infoToster(
          i18n.t("pages.approval_workflow.toast.already_pending", {
            defaultValue: "This action already has a pending approval request.",
          })
        );
        suppressNextErrorToster();
        error.approvalPending = true;
        return Promise.reject(error);
      }

      if (status === 401 && !isLoginCall) {
        // Session explicitly revoked/expired, or a refresh-retry already failed:
        // stop retrying and send the user back to login.
        if (isSessionKilledMessage(msg) || originalRequest._retry) {
          forceLogout(msg);
          return Promise.reject(error);
        }

        originalRequest._retry = true;
        const newToken = await getToken(); // refreshes the access token if expired
        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return instance(originalRequest);
        }
        // No token could be obtained → dead session.
        forceLogout(msg);
      }

      return Promise.reject(error);
    }
  );

  return instance;
};

// API Client Methods Wrapper
const createApiClient = (axiosInstance) => {
  return {
    get: async (url, params, errMsg, extraConfig = {}) => { 
      try {
        const response = await trackPromise(
          axiosInstance.get(url, {
              headers: {
              ...jsonHeaders,
              ...axiosInstance.defaults.headers.common,
              ...(extraConfig.headers || {}),
            },
            params,
          })
        );
        return response;
      } catch (error) {
        console.error("GET request failed:", error);
        throw error;
      }
    },

    post: async (url, data, errMsg, query = null, extraConfig = {}) => {
      try {
        let endpoint = url;
        if (query) {
          const queryString = new URLSearchParams(query).toString();
          endpoint = `${endpoint}?${queryString}`;
        }
        const response = await trackPromise(
          axiosInstance.post(endpoint, data, {
            headers: {
              ...jsonHeaders,
              ...axiosInstance.defaults.headers.common,
              ...(extraConfig.headers || {}),
            },
          })
        );
        return response;
      } catch (error) {
        console.error("POST request failed:", error);
        throw error;
      }
    },

    delet: async (url, params, data, errMsg) => {
      try {
        const endpoint = !!params ? `${url}/${params}` : url;
        const config = {
          headers: jsonHeaders,
        };
        if (data) config.data = data;

        const response = await trackPromise(
          axiosInstance.delete(endpoint, config)
        );
        return response;
      } catch (error) {
        console.error("DELETE request failed:", error);
        throw error;
      }
    },

    put: async (url, data, params, errMsg) => {
      try {
        const endpoint = !!params ? `${url}/${params}` : url;
        const response = await trackPromise(
          axiosInstance.put(endpoint, data, {
            headers: { ...jsonHeaders, ...axiosInstance.defaults.headers.common },
          })
        );
        return response;
      } catch (error) {
        console.error("PUT request failed:", error);
        throw error;
      }
    },

    patch: async (url, data, params, errMsg) => {
      try {
        const endpoint = params ? `${url}/${params}` : url;
        const response = await trackPromise(
          axiosInstance.patch(endpoint, data, {
            headers: jsonHeaders,
          })
        );
        return response;
      } catch (error) {
        throw error;
      }
    },

    postFormData: async (url, data, errMsg, extraConfig = {}) => {
      try {
        const response = await trackPromise(
          axiosInstance.post(url, data, {
            headers: {
              ...formDataHeaders,
              ...axiosInstance.defaults.headers.common,
              ...(extraConfig.headers || {}),
            },
            ...extraConfig, // include other config like timeout, etc.
          })
        );
        return response;
      } catch (error) {
        console.error("POST (form-data) failed:", error);
        throw error;
      }
    },

    putFormData: async (url, data, errMsg, extraConfig = {}) => {
      try {
        const response = await trackPromise(
          axiosInstance.put(url, data, {
            headers: {
              ...formDataHeaders,
              ...axiosInstance.defaults.headers.common,
              ...(extraConfig.headers || {}),
            },
            ...extraConfig, 
          })
        );
        return response;
      } catch (error) {
        console.error("POST (form-data) failed:", error);
        throw error;
      }
    },
    

    setRequestHeader: (key, value) => {
      try {
        axiosInstance.defaults.headers.common[key] = value;
      } catch (error) {
        console.error("Error setting header:", error);
      }
    },
  };
};

const authInstance = createApiInstance(END_POINT.AUTH_BASE_URL);
const bookingInstance = createApiInstance(END_POINT.BASE_URL);
const notificationInstance = createApiInstance(END_POINT.NOTIFICATION_BASE_URL);
const selfCheckInInstance = createApiInstance(END_POINT.BASE_URL, true, false);
 const crmInstance = createApiInstance(END_POINT.CRM_BASE_URL);
const apiClients = {
  authApi: createApiClient(authInstance),
  api: createApiClient(bookingInstance),
  notificationApi: createApiClient(notificationInstance),
  selfcheckinApi: createApiClient(selfCheckInInstance),
  crmApi: createApiClient(crmInstance),
};

const ApiClient = () => apiClients;

export default ApiClient;